/**
 * Hook to build pdfInfo for documents when s3Key is not available.
 * Uses stored data from documents/documentinfo tables plus related entities.
 */

import { useContext, useCallback, useEffect, useState } from 'react';
import { Voucher } from '../api/vouchers/types';
import { buildPdfInfo, PdfInfoBuilderContext } from '../api/vouchers/pdfInfoBuilder';
import vouchersApi from '../api/vouchers/api';
import { Context as ClientsContext } from '../api/clients/context';
import { Context as CompaniesContext } from '../api/companies/context';
import { Context as CurrenciesContext } from '../api/currencies/context';
import { Context as ExchangeRatesContext } from '../api/exchangeRates/context';
import { Context as SupplierAccountsContext } from '../api/supplierAccounts/context';
import { Context as TravelAgencyContext } from '../api/travelAgencyAccounts/context';
import { Context as AuthContext } from '../api/auth/context';
import { Context as UsersContext } from '../api/users/context';
import { getPdfCompanyInfo } from '../api/companies/helpers';
import { Client } from '../api/clients/types';
import { getDataOptions } from '../utils/arrayUtils';

/**
 * Hook that provides a function to build pdfInfo for a voucher/document.
 *
 * Usage:
 * ```typescript
 * const { buildVoucherPdfInfo, isReady } = usePdfInfoBuilder();
 *
 * // When viewing a PDF
 * if (voucher.info?.pdfUrl?.s3Key) {
 *     // Fetch from S3
 * } else if (isReady) {
 *     const pdfInfo = buildVoucherPdfInfo(voucher);
 *     // Use pdfInfo to render PDF
 * }
 * ```
 *
 * For Deposits/Withdrawals that cover receipts, pass coveredDocuments:
 * ```typescript
 * const pdfInfo = buildVoucherPdfInfo(voucher, coveredDocuments);
 * ```
 */
export function usePdfInfoBuilder() {
    const clientsContext = useContext(ClientsContext);
    const companiesContext = useContext(CompaniesContext);
    const currenciesContext = useContext(CurrenciesContext);
    const exchangeRatesContext = useContext(ExchangeRatesContext);
    const supplierAccountsContext = useContext(SupplierAccountsContext);
    const travelAgencyContext = useContext(TravelAgencyContext);
    const authContext = useContext(AuthContext);
    const usersContext = useContext(UsersContext);

    // Load "Issued By" settings from app_config (per doc type toggle)
    const [issuedBySettings, setIssuedBySettings] = useState<Record<string, boolean>>({});
    useEffect(() => {
        vouchersApi.getAppConfig('finance_issued_by_settings')
            .then((result: any) => {
                if (result?.value) {
                    try { setIssuedBySettings(JSON.parse(result.value)); } catch { /* defaults */ }
                }
            })
            .catch(() => {});
    }, []);

    // Static lookup data is prefetched at login (usePrefetchStaticData)

    const hasClient = !!clientsContext.selected;
    const hasCompanies = companiesContext.data?.length > 0 || companiesContext.dataAsArray?.length > 0;
    const hasCurrencies = currenciesContext.dataAsArray?.length > 0;
    const isReady = hasClient && hasCompanies && hasCurrencies;

    /**
     * Build pdfInfo for a voucher from stored data
     * @param voucher - The voucher to build pdfInfo for
     * @param coveredDocuments - Optional array of covered documents (for Deposit/Withdrawal)
     * @param serviceData - Optional full service data (for Voucher/Refund types)
     * @param overrides - Optional override data (e.g. pre-fetched travelAgencyAccount)
     */
    const buildVoucherPdfInfo = useCallback(
        (voucher: Voucher, coveredDocuments?: Voucher[], serviceData?: any, overrides?: { travelAgencyAccount?: any; supplierAccount?: any; bankAccount?: any }) => {
            if (!isReady) {
                return null;
            }

            let supplierAccount = overrides?.supplierAccount;
            let travelAgencyAccount = overrides?.travelAgencyAccount;

            if (!supplierAccount && voucher.sourceType === 'supplier' && voucher.sourceId) {
                const sourceIdStr = String(voucher.sourceId);
                supplierAccount = getDataOptions(supplierAccountsContext.data).find(
                    (s) => String(s._id) === sourceIdStr
                );
            }
            // For TRC/Deposit/Withdrawal: source is 'account', not 'supplier'.
            // Try to find supplier from covered documents chain.
            if (!supplierAccount && voucher.targetType !== 'supplier' && coveredDocuments?.length) {
                for (const covDoc of coveredDocuments) {
                    if (covDoc.sourceType === 'supplier' && covDoc.sourceId) {
                        supplierAccount = getDataOptions(supplierAccountsContext.data).find(
                            (s) => String(s._id) === String(covDoc.sourceId)
                        );
                        if (supplierAccount) break;
                    }
                    if (covDoc.targetType === 'supplier' && covDoc.targetId) {
                        supplierAccount = getDataOptions(supplierAccountsContext.data).find(
                            (s) => String(s._id) === String(covDoc.targetId)
                        );
                        if (supplierAccount) break;
                    }
                }
            }

            // Resolve supplier from stored name and service data, then pick the best one.
            // Some Galor vouchers have a billing intermediary as supplierName (e.g., "BSP")
            // while the actual airline is on the service (e.g., "IBERIA" with full address/phones).
            // Others have the correct supplier name on the voucher (e.g., "TAL AVIATION") while
            // the service references a different supplier (e.g., "AIR EUROPA").
            // Heuristic: prefer whichever supplier has real contact data (address/phones).
            let nameBasedSupplier: any;
            let serviceBasedSupplier: any;

            if (!supplierAccount && (voucher.info as any)?.supplierName) {
                const storedName = (voucher.info as any).supplierName;
                nameBasedSupplier = getDataOptions(supplierAccountsContext.data).find(
                    (s) => s.supplierCompanyName === storedName
                );
            }

            if (!supplierAccount && nameBasedSupplier && serviceData?.supplierAccount) {
                // Both stored name and current service supplier exist
                // If they match, use serviceData (has full address/phones from getFullServiceById)
                // If they differ, supplier was changed after voucher was issued — use stored name (snapshot)
                if (nameBasedSupplier.supplierCompanyName === serviceData.supplierAccount.supplierCompanyName) {
                    supplierAccount = serviceData.supplierAccount;
                } else {
                    supplierAccount = nameBasedSupplier;
                }
            } else if (!supplierAccount && serviceData?.supplierAccount) {
                supplierAccount = serviceData.supplierAccount;
            } else if (!supplierAccount) {
                supplierAccount = nameBasedSupplier;
            }

            if (!travelAgencyAccount && voucher.sourceType === 'agent' && voucher.sourceId) {
                const sourceIdStr = String(voucher.sourceId);
                travelAgencyAccount = getDataOptions(travelAgencyContext.data).find(
                    (a) => String(a._id) === sourceIdStr
                );
            }

            // Also check target for agent info (some docs have agent as target)
            // Use string comparison to handle ObjectId vs string mismatch
            if (!travelAgencyAccount && voucher.targetType === 'agent' && voucher.targetId) {
                const targetIdStr = String(voucher.targetId);
                travelAgencyAccount = getDataOptions(travelAgencyContext.data).find(
                    (a) => String(a._id) === targetIdStr
                );
            }

            // Fallback for ACI: use reservingTravelAgent from info (set by postprocessor)
            if (!travelAgencyAccount && voucher.info?.reservingTravelAgent) {
                const agentIdStr = String(voucher.info.reservingTravelAgent);
                travelAgencyAccount = getDataOptions(travelAgencyContext.data).find(
                    (a) => String(a._id) === agentIdStr
                );
            }

            // Build user ID → name map for resolving numeric createdBy (prefer username over full name)
            const usersById: Record<string | number, string> = {};
            const usersArr = getDataOptions(usersContext.data);
            for (const u of usersArr) {
                if (u._id && (u.username || u.name)) usersById[u._id] = u.username || u.name;
            }
            // Make sure the signed-in user resolves even when the users list they are
            // allowed to read is partial — this only ever applies to documents whose
            // createdBy is their own id, never to documents with no createdBy at all.
            const currUserId = (authContext.currUser as any)?._id;
            const currUserName = (authContext.currUser as any)?.username || authContext.currUser?.name || '';
            if (currUserId && currUserName && !usersById[currUserId]) {
                usersById[currUserId] = currUserName;
            }

            const context: PdfInfoBuilderContext = {
                companyInfo: getPdfCompanyInfo(companiesContext),
                client: clientsContext.selected as Client,
                currencies: currenciesContext.dataAsArray,
                getConvertRate: exchangeRatesContext.getConvertRate,
                // Deliberately empty — there is no viewer fallback. A document with no
                // createdBy (a tour-registration receipt, say, written by a background
                // job) must not be attributed to whoever happens to open it; the
                // "Issued by" line is dropped below instead.
                userName: '',
                usersById,
                supplierAccount,
                travelAgencyAccount,
                bankAccount: overrides?.bankAccount,
                coveredDocuments,
                serviceData
            };

            const pdfInfo = buildPdfInfo(voucher, context);
            // Nothing on the document identifies who issued it (createdBy is null, and
            // no stored userName) — omit the "Issued by" line rather than naming anyone.
            if (!String(pdfInfo.userName || '').trim()) {
                pdfInfo.showIssuedBy = false;
            }
            // Apply "Issued By" toggle from Finance General Settings
            const docType = voucher.type || '';
            if (docType in issuedBySettings && issuedBySettings[docType] === false) {
                pdfInfo.showIssuedBy = false;
            }
            return pdfInfo;
        },
        [
            isReady,
            clientsContext.selected,
            companiesContext,
            currenciesContext.dataAsArray,
            exchangeRatesContext.getConvertRate,
            supplierAccountsContext.data,
            travelAgencyContext.data,
            authContext.currUser,
            usersContext.data,
            issuedBySettings
        ]
    );

    /**
     * Get pdfInfo - either from stored data or build it
     * @param voucher - The voucher to get pdfInfo for
     * @param coveredDocuments - Optional array of covered documents (for Deposit/Withdrawal)
     * @param serviceData - Optional full service data (for Voucher/Refund types)
     * @param overrides - Optional override data (e.g. pre-fetched travelAgencyAccount)
     */
    const getPdfInfo = useCallback(
        (voucher: Voucher, coveredDocuments?: Voucher[], serviceData?: any, overrides?: { travelAgencyAccount?: any; supplierAccount?: any; bankAccount?: any }) => {
            // Always build pdfInfo dynamically from stored data.
            // This ensures the builder output is always used (consistent rendering).
            // Previously we'd return stored pdfInfo if available, but that caused
            // mismatches between inline-created and dynamically-built PDFs.
            return buildVoucherPdfInfo(voucher, coveredDocuments, serviceData, overrides);
        },
        [buildVoucherPdfInfo]
    );

    return {
        buildVoucherPdfInfo,
        getPdfInfo,
        isReady
    };
}

export default usePdfInfoBuilder;
